import { FC } from 'react';

import { CheckboxFilterTree } from '@telialt/common-checkbox-filter-tree/src/CheckboxFilterTree';
import { ResponsiveLabel } from '@telialt/common-checkbox-filter-tree/src/LabelComponents';
import { FilterNode } from '@telialt/common-checkbox-filter-tree/src/model';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import { FacetHierarchy, FilterHierarchy } from '../../model';
import { FacetItem } from '../FacetItem/FacetItem';
import { FacetContainer } from './Facets.styles';

type Props = {
    facet: FacetHierarchy;
    filter?: FilterHierarchy;
    onChange: (fieldName: string, filter?: FilterHierarchy) => void;
    showCount?: boolean;
};

const mapFacetItemsToFilterNodes = (showCount: boolean, items?: FacetHierarchy['items']): FilterNode[] =>
    items?.map((item) => ({
        title: item.title,
        count: showCount ? item.count : undefined,
        filters: mapFacetItemsToFilterNodes(showCount, item.children),
    }));

const countFilterNodes = (filterNodes: FilterNode[]) => {
    return filterNodes.reduce<number>((acc, node) => {
        const childrenNodesCount = node.filters?.length > 0 ? countFilterNodes(node.filters) : 0;

        return acc + childrenNodesCount + 1;
    }, 0);
};

export const HierarchyFacet: FC<Props> = ({ facet, filter, onChange, showCount }) => {
    const { t } = useTranslations();

    const filters = mapFacetItemsToFilterNodes(showCount, facet.items);

    const handleSelect = (values: string[][]) => {
        onChange(
            facet.key,
            values.length > 0
                ? {
                      level: 0,
                      terms: values.map((path) => path.join(' > ')),
                  }
                : undefined,
        );
    };

    const nodesCount = countFilterNodes(filters);

    const selected = filter?.terms?.map((path) => path.split(' > '));

    return (
        <FacetItem id={facet.key} label={facet.label} facetType="hierarchy">
            <FacetContainer>
                <CheckboxFilterTree
                    filters={filters}
                    selected={selected}
                    selectionBehavior="fuzzy"
                    searchEnabled={nodesCount > 10}
                    searchPlaceholder={t['search.results.facets.search_placeholder']}
                    searchAllyLabel={t['search.results.facets.search_a11y_label']}
                    searchClearButtonAllyLabel={t['search.results.facets.search_clear_label']}
                    maxVisibleItems={3}
                    revealingListDepth="all-levels"
                    showLessText={t['search.results.facets.collapse']}
                    showMoreText={t['search.results.facets.expand']}
                    onSelect={handleSelect}
                    Label={ResponsiveLabel}
                    aria-label={facet.label}
                />
            </FacetContainer>
        </FacetItem>
    );
};
